import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { AnalysisType, Rating } from '@weave/schema-gen-ts/dist/shared/call-intelligence/enums.pb';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  ChecklistField,
  ChecklistMenuField,
  ContentLoader,
  Modal,
  ModalControlModalProps,
  Text,
  TextareaField,
  useControlledField,
  useFormField,
} from '@frontend/design-system';
import { useOptionsProvider } from '../../hooks/use-options-provider';
import FeedbackIssueInput from '../input/feedback-issue.input';
import { RatingInputField } from '../input/rating-input-field';

export type Feedback = {
  allowWeaveToContact: boolean;
  issues?: AnalysisType[];
  message?: string;
  rating: Rating;
};

type Props = {
  disableCloseOnOverlayClick?: boolean;
  isLoading?: boolean;
  modalProps: ModalControlModalProps;
  onSubmit: (feedback: Feedback) => void;
  productName: string;
  showAllowToContact?: boolean;
  subtitle?: string;
  title?: string;
  trackingIdBase?: string;
  isAIFeedback?: boolean;
  isAIPositiveFeedback?: boolean;
};

export const FeedbackModal = ({
  disableCloseOnOverlayClick,
  isLoading,
  modalProps,
  onSubmit,
  productName,
  showAllowToContact = true,
  isAIFeedback,
  isAIPositiveFeedback,
  subtitle,
  title,
  trackingIdBase = 'feedback-modal',
}: Props) => {
  const { t } = useTranslation('analytics');

  const [allowWeaveToContact, setAllowWeaveToContact] = useState<[string]>(['yes']);
  const [message, setMessage] = useState<string>();
  const [rating, setRating] = useState<Rating | undefined>();

  const { getOptions } = useOptionsProvider();

  const feedbackOptions = getOptions('feedbackIssueTypes', 'array');

  const feedbackChecklistProps = useFormField({
    type: 'checklist',
  });

  const messageField = useControlledField({
    onChange: setMessage,
    type: 'text',
    value: message,
  });

  const allowWeaveContactField = useControlledField({
    onChange: setAllowWeaveToContact,
    type: 'checklist',
    value: allowWeaveToContact,
  });

  const handleSubmit = () => {
    onSubmit({
      allowWeaveToContact: allowWeaveToContact.includes('yes'),
      message,
      rating: rating!,
      issues: feedbackChecklistProps.value as AnalysisType[],
    });
  };

  const handleRemoveIssueLabel = (value: string) => {
    const updatedValues = feedbackChecklistProps.value.filter((item) => item === value);

    // Use the same name as in your ChecklistMenuField
    feedbackChecklistProps.onChange({
      name: 'issues-selector',
      value: updatedValues,
    });
  };

  useEffect(() => {
    if (!modalProps.show) {
      setAllowWeaveToContact(['yes']);
      setMessage(undefined);
      setRating(undefined);
      feedbackChecklistProps.onChange({ name: 'checklist', value: [] });
    }
  }, [modalProps.show]);

  return (
    <Modal
      {...modalProps}
      css={styles.modal}
      disableCloseOnEscape={isLoading}
      disableCloseOnOverlayClick={disableCloseOnOverlayClick || isLoading}
      maxWidth={600}
    >
      <Modal.Header onClose={modalProps.onClose}>
        {title || t('How would you rate your experience with {{productName}}?', { productName })}
      </Modal.Header>
      <Modal.Body>
        <Text>
          {subtitle ||
            t(
              "Please share as much detail about what's working well, issues you are experiencing, or suggestions for how we can improve the product."
            )}
        </Text>
        {!isAIFeedback && <RatingInputField onChange={setRating} trackingIdBase={trackingIdBase} value={rating} />}
        {isAIFeedback && !isAIPositiveFeedback && (
          <>
            <ChecklistMenuField
              css={styles.checklistWrapper}
              {...feedbackChecklistProps}
              customRender={({ selectedKeys }) => {
                const selectedLabels: string[] = selectedKeys
                  .map((key) => feedbackOptions.find((option) => option.id === key)?.label)
                  .filter((label): label is string => Boolean(label));

                return (
                  <FeedbackIssueInput
                    selectedLabels={selectedLabels}
                    selectedKeys={selectedKeys}
                    onRemoveLabel={handleRemoveIssueLabel}
                  />
                );
              }}
              label={t('Select Issues (Optional)')}
              name='issues-selector'
              showSearchBar={false}
              showSelectionBar={false}
              sortLabelsBySelection={false}
              placement='bottom-start'
            >
              {feedbackOptions.map((value) => (
                <ChecklistMenuField.Option key={value.id} value={value.id}>
                  {value.label}
                </ChecklistMenuField.Option>
              ))}
            </ChecklistMenuField>
          </>
        )}
        <TextareaField
          {...messageField}
          css={[
            showAllowToContact &&
              css`
                margin-bottom: ${theme.spacing(2)};
              `,
          ]}
          data-trackingid={`${trackingIdBase}-message`}
          label={t('Feedback (Optional)')}
          name='product-feedback'
        />
        {showAllowToContact && (
          <ChecklistField {...allowWeaveContactField} label='' name='allowContact'>
            <ChecklistField.Option name='yes' trackingId={`${trackingIdBase}-allow-weave-contact`}>
              {t('Allow Weave to contact me about my response')}
            </ChecklistField.Option>
          </ChecklistField>
        )}
      </Modal.Body>
      <Modal.Actions
        disablePrimary={isAIFeedback ? !(message || feedbackChecklistProps.value.length > 0) : !rating}
        onPrimaryClick={handleSubmit}
        onSecondaryClick={modalProps.onClose}
        primaryLabel={t('Submit')}
        primaryTrackingId={`submit-product-feedback-for-${productName.toLowerCase().replaceAll(' ', '-')}`}
        secondaryLabel={t('Cancel')}
        secondaryTrackingId={`cancel-product-feedback-for-${productName.toLowerCase().replaceAll(' ', '-')}`}
      />
      <ContentLoader show={isLoading} />
    </Modal>
  );
};

const styles = {
  modal: css`
    width: 100%;
    @media screen and (min-width: 648px) {
      width: 100%;
    }
  `,
  chip: css`
    background-color: ${theme.colors.primary5};
    font-size: ${theme.fontSize(12)};
    border: none;
    color: ${theme.colors.neutral90};
    max-width: ${theme.spacing(28)};

    button {
      padding: ${theme.spacing(0.5)};
      svg {
        width: ${theme.spacing(1)};
        height: ${theme.spacing(1)};
        color: ${theme.colors.neutral90};
      }
    }
  `,
  checklistWrapper: css`
    div:last-child {
      gap: ${theme.spacing(0)} !important;
    }

    input {
      height: ${theme.spacing(3)};
    }
  `,
};
